/**
 * PSV-1729 — circular radar/minimap HUD widget (Starblast HUD parity).
 *
 * A fixed-screen disc anchored on the right edge, sitting just above the
 * bottom-right UpgradePanel. The whole circular arena is projected onto
 * the disc: world coordinates divide by `arenaRadius` and multiply by the
 * disc radius, so the disc's rim IS the arena border. Anything the server
 * reports outside the arena (transient overshoot, death drift) is clamped
 * to the rim instead of escaping the widget.
 *
 * Blip legend:
 *   - own ship  — bright cyan dot + short heading tick (me.angle)
 *   - others    — red dots
 *   - asteroids — grey dots, medium/large only (radius >= 25), sized
 *                 1.5–3px by world radius so big rocks read bigger
 *
 * Redraws are throttled to 10Hz: update(deltaMs) accumulates elapsed time
 * and repaints only once >=100ms has passed. That matches the server's
 * 10Hz motion broadcast cadence — repainting faster would just redraw the
 * same snapshot — and keeps the single-Graphics clear/refill cheap.
 * `getSnapshot()` is only invoked on an actual redraw, never per frame.
 *
 * Rendering sticks to the Graphics subset available in the test mock
 * (fillStyle/fillCircle/lineStyle/lineBetween/clear + rect variants):
 * the border ring is stroked as 32 short lineBetween chords rather than
 * strokeCircle, and the own-ship marker is a fillCircle dot plus a
 * lineBetween heading tick rather than a fillTriangle.
 */

import Phaser from 'phaser';

/** World-state slice the radar samples on each (throttled) redraw. */
export interface RadarSnapshot {
  arenaRadius: number;
  me: { x: number; y: number; angle: number } | null;
  /** PSV-1886 — `leader` marks the #1 pilot (gold blip: walking bounty). */
  ships: Array<{ x: number; y: number; leader?: boolean }>;
  asteroids: Array<{ x: number; y: number; radius: number }>;
  /** PSV-1887 — hot-zone radius (world px); brighter while eventActive. */
  hotZoneRadius?: number;
  eventActive?: boolean;
}

const DISC_RADIUS = 72;
const MARGIN = 16;
// Clears the 110px-tall UpgradePanel + bottom margin: center X is
// DISC_RADIUS + 16 in from the right edge, center Y is one disc radius
// above the panel's top edge.
const UPGRADE_PANEL_HEIGHT = 110;
const CENTER_OFFSET_X = 88; // DISC_RADIUS + 16
const CENTER_OFFSET_Y = UPGRADE_PANEL_HEIGHT + MARGIN + DISC_RADIUS; // 198

const REDRAW_INTERVAL_MS = 100; // 10Hz — matches server broadcast cadence
const RING_SEGMENTS = 32; // chord count for the lineBetween border ring

const BG_COLOR = 0x0b101c;
const BG_ALPHA = 0.85;
const BORDER_COLOR = 0x2c3a5c;
const CROSSHAIR_COLOR = 0x2c3a5c;
const CROSSHAIR_ALPHA = 0.4;
const OWN_SHIP_COLOR = 0x35c4f0;
const OWN_SHIP_DOT_RADIUS = 3;
const OWN_SHIP_TICK_LENGTH = 7; // heading tick, from dot center outward
const OTHER_SHIP_COLOR = 0xff5577;
const OTHER_SHIP_DOT_RADIUS = 2.5;
const ASTEROID_COLOR = 0x556077;
const ASTEROID_MIN_WORLD_RADIUS = 25; // medium/large tiers only
// Asteroid dots scale 1.5px (radius 25) → 3px (radius 40+).
const ASTEROID_DOT_MIN = 1.5;
const ASTEROID_DOT_MAX = 3;
const ASTEROID_WORLD_RADIUS_MAX = 40;

export class RadarMinimap {
  private readonly scene: Phaser.Scene;
  private readonly getSnapshot: () => RadarSnapshot;

  /** Single Graphics — cleared and fully repainted on each redraw. */
  private graphics: Phaser.GameObjects.Graphics | null = null;
  /** Time since last redraw; primed so the first update() paints at once. */
  private sinceRedrawMs = REDRAW_INTERVAL_MS;
  private destroyed = false;

  constructor(scene: Phaser.Scene, getSnapshot: () => RadarSnapshot) {
    this.scene = scene;
    this.getSnapshot = getSnapshot;

    const cam = this.scene.cameras.main;
    // Graphics is positioned at the disc center; all painting below is in
    // disc-local coordinates around (0, 0).
    this.graphics = this.scene.add
      .graphics()
      .setPosition(cam.width - CENTER_OFFSET_X, cam.height - CENTER_OFFSET_Y)
      .setScrollFactor(0)
      .setDepth(18);

    this.redraw();
  }

  /**
   * Called per Phaser frame from ArenaController.update(). Accumulates
   * elapsed time and repaints at most once per REDRAW_INTERVAL_MS.
   */
  update(deltaMs: number): void {
    if (this.destroyed) return;
    this.sinceRedrawMs += deltaMs;
    if (this.sinceRedrawMs < REDRAW_INTERVAL_MS) return;
    this.sinceRedrawMs = 0;
    this.redraw();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.graphics?.destroy();
    this.graphics = null;
  }

  // ---------- private ----------

  /** Full repaint: disc, ring, cross-hair, then blips from a fresh snapshot. */
  private redraw(): void {
    if (this.destroyed || this.graphics === null) return;
    const g = this.graphics;
    const snapshot = this.getSnapshot();

    g.clear();

    // Dark translucent disc — the whole arena, edge-to-edge.
    g.fillStyle(BG_COLOR, BG_ALPHA);
    g.fillCircle(0, 0, DISC_RADIUS);

    // Faint cross-hair through the arena center.
    g.lineStyle(1, CROSSHAIR_COLOR, CROSSHAIR_ALPHA);
    g.lineBetween(-DISC_RADIUS, 0, DISC_RADIUS, 0);
    g.lineBetween(0, -DISC_RADIUS, 0, DISC_RADIUS);

    // Arena border ring — the mock Graphics has no strokeCircle, so the
    // ring is 32 short chords; at 72px radius the polygon is
    // indistinguishable from a circle.
    g.lineStyle(1, BORDER_COLOR, 1);
    for (let i = 0; i < RING_SEGMENTS; i++) {
      const a0 = (i / RING_SEGMENTS) * Math.PI * 2;
      const a1 = ((i + 1) / RING_SEGMENTS) * Math.PI * 2;
      g.lineBetween(
        Math.cos(a0) * DISC_RADIUS,
        Math.sin(a0) * DISC_RADIUS,
        Math.cos(a1) * DISC_RADIUS,
        Math.sin(a1) * DISC_RADIUS,
      );
    }

    // Guard against a zero/negative arenaRadius (pre-MatchConfig frame):
    // skip blips rather than divide by zero.
    if (snapshot.arenaRadius <= 0) return;

    // Asteroids first (lowest visual priority) — medium/large only.
    g.fillStyle(ASTEROID_COLOR, 1);
    for (const asteroid of snapshot.asteroids) {
      if (asteroid.radius < ASTEROID_MIN_WORLD_RADIUS) continue;
      const p = this.project(asteroid.x, asteroid.y, snapshot.arenaRadius);
      const sizeT = Math.min(
        1,
        (asteroid.radius - ASTEROID_MIN_WORLD_RADIUS) /
          (ASTEROID_WORLD_RADIUS_MAX - ASTEROID_MIN_WORLD_RADIUS),
      );
      const dotRadius =
        ASTEROID_DOT_MIN + (ASTEROID_DOT_MAX - ASTEROID_DOT_MIN) * sizeT;
      g.fillCircle(p.x, p.y, dotRadius);
    }

    // PSV-1887 — hot-zone ring: faint gold normally, brighter while an
    // event runs. Drawn as chords (mock-safe, same as the border ring).
    if (typeof snapshot.hotZoneRadius === 'number' && snapshot.hotZoneRadius > 0) {
      const zr =
        (snapshot.hotZoneRadius / Math.max(1, snapshot.arenaRadius)) * DISC_RADIUS;
      g.lineStyle(1, 0xffd84a, snapshot.eventActive === true ? 0.9 : 0.35);
      const SEGS = 20;
      let px = zr;
      let py = 0;
      for (let i = 1; i <= SEGS; i++) {
        const a = (i / SEGS) * Math.PI * 2;
        const nx = Math.cos(a) * zr;
        const ny = Math.sin(a) * zr;
        g.lineBetween(px, py, nx, ny);
        px = nx;
        py = ny;
      }
    }

    // Other ships — red dots over the asteroid layer. The current #1
    // (PSV-1886 walking bounty) paints gold and slightly bigger, so the
    // hunt target reads at a glance.
    for (const ship of snapshot.ships) {
      const p = this.project(ship.x, ship.y, snapshot.arenaRadius);
      if (ship.leader === true) {
        g.fillStyle(0xffd84a, 1);
        g.fillCircle(p.x, p.y, OTHER_SHIP_DOT_RADIUS + 1.5);
      } else {
        g.fillStyle(OTHER_SHIP_COLOR, 1);
        g.fillCircle(p.x, p.y, OTHER_SHIP_DOT_RADIUS);
      }
    }

    // Own ship last (always on top): cyan dot + heading tick along
    // me.angle so the player can read their facing at a glance.
    if (snapshot.me !== null) {
      const p = this.project(snapshot.me.x, snapshot.me.y, snapshot.arenaRadius);
      g.fillStyle(OWN_SHIP_COLOR, 1);
      g.fillCircle(p.x, p.y, OWN_SHIP_DOT_RADIUS);
      g.lineStyle(1.5, OWN_SHIP_COLOR, 1);
      g.lineBetween(
        p.x,
        p.y,
        p.x + Math.cos(snapshot.me.angle) * OWN_SHIP_TICK_LENGTH,
        p.y + Math.sin(snapshot.me.angle) * OWN_SHIP_TICK_LENGTH,
      );
    }
  }

  /**
   * Map a world position onto the disc: divide by arenaRadius, multiply
   * by the disc radius. Positions outside the arena clamp to the rim so
   * every blip stays inside the widget.
   */
  private project(
    worldX: number,
    worldY: number,
    arenaRadius: number,
  ): { x: number; y: number } {
    let x = (worldX / arenaRadius) * DISC_RADIUS;
    let y = (worldY / arenaRadius) * DISC_RADIUS;
    const dist = Math.sqrt(x * x + y * y);
    if (dist > DISC_RADIUS) {
      const scale = DISC_RADIUS / dist;
      x *= scale;
      y *= scale;
    }
    return { x, y };
  }
}
